import React, { useEffect, useMemo, useState } from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, List,
    ListItemButton, ListItemText, Radio, Stack, TextField, Typography, useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import livechatApi from "./livechatApi";
import { TRANSFER_NOTE_MAX_LENGTH } from "./livechatConstants";
import { PASTEL } from "./livechatTheme";

const chipSx = { height: 18, fontSize: 10, fontWeight: 700, "& .MuiChip-label": { px: 0.75 } };

/**
 * MTN-956 — dialog "Alihkan Chat" (khusus Tim CS, privilege session.transfer). Tim CS memilih
 * sales tujuan (member whitelist autoleads, boleh offline) dan boleh menulis catatan internal untuk
 * sales itu (tidak terkirim ke customer). Sales terakhir yang menangani ditandai supaya mudah dipilih
 * kembali. `onSubmit({toAgentId, note})` ditangani index.jsx (request + refresh + snackbar).
 */
export default function TransferChatDialog({ open, session, onClose, onSubmit }) {
    const isMobile = useMediaQuery("(max-width:600px)");
    const [agents, setAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [note, setNote] = useState("");
    const [keyword, setKeyword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const lastOwnerId = session?.assignedAgentId ?? null;

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setSelectedId(lastOwnerId);
        setNote("");
        setKeyword("");
        setError(null);
        setLoading(true);
        livechatApi
            .listAgents()
            .then(({ data }) => { if (!cancelled) setAgents(data.data || []); })
            .catch((err) => { if (!cancelled) setError(err?.response?.data?.message || "Gagal memuat daftar sales"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, lastOwnerId]);

    // Sales terakhir yang menangani di paling atas, lalu yang siap / online.
    const visibleAgents = useMemo(() => {
        const q = keyword.trim().toLowerCase();
        const rank = (a) => (Number(a.userId) === Number(lastOwnerId) ? 0 : a.online && a.acceptingLeads ? 1 : a.online ? 2 : 3);
        return agents
            .filter((a) => !q || (a.userName || "").toLowerCase().includes(q))
            .sort((a, b) => rank(a) - rank(b) || (a.userName || "").localeCompare(b.userName || ""));
    }, [agents, keyword, lastOwnerId]);

    const handleSubmit = async () => {
        if (selectedId == null) return;
        setSubmitting(true);
        setError(null);
        try {
            await onSubmit({ toAgentId: selectedId, note: note.trim() || undefined });
        } catch (err) {
            setError(err?.response?.data?.message || "Gagal mengalihkan percakapan");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth fullScreen={isMobile}>
            <DialogTitle sx={{ pb: 0.5 }}>Alihkan Chat</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Percakapan dengan <strong>{session?.customerName}</strong> akan kembali aktif di tab Milik Saya sales tujuan.
                </Typography>
                {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
                <TextField
                    size="small" fullWidth placeholder="Cari sales"
                    value={keyword} onChange={(e) => setKeyword(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: "text.disabled" }} /></InputAdornment>,
                    }}
                />
                <Box sx={{ mt: 1, border: `1px solid ${PASTEL.border}`, borderRadius: 1.5, maxHeight: isMobile ? "none" : 280, overflowY: "auto" }}>
                    {loading ? (
                        <Stack alignItems="center" sx={{ py: 3 }}><CircularProgress size={22} sx={{ color: PASTEL.primary }} /></Stack>
                    ) : visibleAgents.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                            Tidak ada sales Live Chat
                        </Typography>
                    ) : (
                        <List dense disablePadding>
                            {visibleAgents.map((a) => {
                                const isLast = Number(a.userId) === Number(lastOwnerId);
                                return (
                                    <ListItemButton
                                        key={a.userId}
                                        selected={Number(selectedId) === Number(a.userId)}
                                        onClick={() => setSelectedId(a.userId)}
                                        sx={{ gap: 1, "&.Mui-selected": { backgroundColor: PASTEL.primarySoft } }}
                                    >
                                        <Radio
                                            size="small" checked={Number(selectedId) === Number(a.userId)} tabIndex={-1}
                                            inputProps={{ "aria-label": a.userName }}
                                            sx={{ p: 0.5, "&.Mui-checked": { color: PASTEL.primary } }}
                                        />
                                        <ListItemText
                                            primary={a.userName}
                                            primaryTypographyProps={{ variant: "body2", fontWeight: 600, noWrap: true }}
                                            secondaryTypographyProps={{ component: "div" }}
                                            secondary={
                                                <Stack direction="row" spacing={0.5} sx={{ mt: 0.25, flexWrap: "wrap", rowGap: 0.5 }}>
                                                    {isLast && <Chip size="small" label="Terakhir menangani" sx={{ ...chipSx, backgroundColor: PASTEL.primary, color: "#fff" }} />}
                                                    {a.online && a.acceptingLeads ? (
                                                        <Chip size="small" label="Siap menerima lead" sx={{ ...chipSx, backgroundColor: "rgba(46,125,50,0.12)", color: "#2e7d32" }} />
                                                    ) : a.online ? (
                                                        <Chip size="small" label="Online" sx={{ ...chipSx, backgroundColor: "rgba(46,125,50,0.12)", color: "#2e7d32" }} />
                                                    ) : (
                                                        <Chip size="small" label="Offline" variant="outlined" sx={{ ...chipSx, color: "text.secondary" }} />
                                                    )}
                                                </Stack>
                                            }
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    )}
                </Box>
                <TextField
                    label="Catatan untuk sales (opsional)"
                    placeholder="Mis. customer menanyakan status pengiriman"
                    fullWidth multiline minRows={2} maxRows={5} size="small"
                    value={note}
                    onChange={(e) => setNote(e.target.value.slice(0, TRANSFER_NOTE_MAX_LENGTH))}
                    helperText={`Hanya terlihat oleh tim internal, tidak terkirim ke customer · ${note.length}/${TRANSFER_NOTE_MAX_LENGTH}`}
                    sx={{ mt: 2 }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none" }}>Batal</Button>
                <Button
                    onClick={handleSubmit} disabled={submitting || selectedId == null} variant="contained"
                    sx={{ textTransform: "none", backgroundColor: PASTEL.primary, "&:hover": { backgroundColor: PASTEL.primaryDark } }}
                >
                    {submitting ? "Mengalihkan..." : "Alihkan"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
